import type { PublicBuildingState as BuildingState } from "@point/core";
import { useEffect, useLayoutEffect, useMemo, useRef } from "react";
import { BlueprintBuilding } from "./BlueprintBuilding.tsx";
import { computeCityBounds } from "./bounds.ts";
import { CityGround } from "./CityGround.tsx";
import { CityRoads } from "./CityRoads.tsx";
import { depthKey } from "./iso.ts";
import { BP } from "./theme.ts";
import { usePanZoom } from "./usePanZoom.ts";

type FitDims = { width: number; height: number };

function computeFitPan(rect: FitDims, b: FitDims, cx: number, cy: number) {
	const w = b.width || 400;
	const h = b.height || 400;
	const k = Math.min((rect.width * 0.82) / w, (rect.height * 0.55) / h, 1.25);
	return {
		k,
		x: rect.width / 2 - cx * k,
		y: rect.height * 0.48 - cy * k,
	};
}

type CitySceneProps = {
	buildings: BuildingState[];
	selectedId: string | null;
	onSelect: (id: string | null) => void;
	onHoverBuilding?: (appId: string | null, screenX: number, screenY: number) => void;
	/** When a building sheet is open — hide peripheral chrome. */
	detailOpen?: boolean;
};

export function CityScene({ buildings, selectedId, onSelect, onHoverBuilding, detailOpen = false }: CitySceneProps) {
	const bounds = useMemo(() => computeCityBounds(buildings), [buildings]);
	const sorted = useMemo(() => [...buildings].sort((a, b) => depthKey(a.gridPos[0], a.gridPos[1]) - depthKey(b.gridPos[0], b.gridPos[1])), [buildings]);

	const cx = (bounds.minX + bounds.maxX) / 2;
	const cy = (bounds.minY + bounds.maxY) / 2;

	const { elRef, pan, setPan, handlers } = usePanZoom(
		{ x: 0, y: 0, k: 0.85 },
		{
			onShortPointerUp: ({ startTarget }) => {
				const hit = startTarget?.closest?.("[data-building-id]");
				const id = hit?.getAttribute("data-building-id");
				if (id) onSelect(id);
				else onSelect(null);
			},
		},
	);

	const cityKey = useMemo(
		() =>
			buildings.length === 0
				? ""
				: [...buildings]
						.map((b) => b.app._id)
						.sort()
						.join("\0"),
		[buildings],
	);

	const boundsRef = useRef(bounds);
	const centerRef = useRef({ cx, cy });
	const countRef = useRef(buildings.length);
	boundsRef.current = bounds;
	centerRef.current = { cx, cy };
	countRef.current = buildings.length;

	/** Fit when the set of apps changes — not on every live Convex refresh of the same city. */
	useLayoutEffect(() => {
		const el = elRef.current;
		if (!el || cityKey === "") return;

		const rect = el.getBoundingClientRect();
		if (rect.width < 10 || rect.height < 10) return;

		const b = boundsRef.current;
		const { cx: ix, cy: iy } = centerRef.current;
		setPan(computeFitPan(rect, b, ix, iy));
	}, [cityKey, elRef, setPan]);

	/** Refit when the viewport grows or shrinks (orientation, split view, resize). */
	useEffect(() => {
		const el = elRef.current;
		if (!el) return;

		const fit = () => {
			if (countRef.current === 0) return;
			const r = el.getBoundingClientRect();
			if (r.width < 10 || r.height < 10) return;
			const b = boundsRef.current;
			const { cx: ix, cy: iy } = centerRef.current;
			setPan(computeFitPan(r, b, ix, iy));
		};

		const ro = new ResizeObserver(fit);
		ro.observe(el);
		return () => ro.disconnect();
	}, [elRef, setPan]);

	return (
		<div ref={elRef} className="h-full w-full select-none touch-none" style={{ touchAction: "none", background: BP.bg }} {...handlers}>
			<svg className="block h-full w-full" aria-label="Portfolio city">
				<g transform={`translate(${pan.x} ${pan.y}) scale(${pan.k}) translate(${-cx} ${-cy})`}>
					<CityGround minX={bounds.minX - 100} maxX={bounds.maxX + 100} minY={bounds.minY - 100} maxY={bounds.maxY + 100} />
					<CityRoads buildings={buildings} />
					{sorted.map((b, i) => (
						<g
							key={b.app._id}
							data-building-id={b.app._id}
							style={{ cursor: "pointer" }}
							onMouseEnter={(e) => onHoverBuilding?.(b.app._id, e.clientX, e.clientY)}
							onMouseMove={(e) => onHoverBuilding?.(b.app._id, e.clientX, e.clientY)}
							onMouseLeave={() => onHoverBuilding?.(null, 0, 0)}
						>
							<BlueprintBuilding building={b} selected={selectedId === b.app._id} appearIndex={i} />
						</g>
					))}
				</g>
			</svg>
			{!detailOpen ? (
				<p
					className="pointer-events-none absolute bottom-3 left-1/2 -translate-x-1/2 text-center text-[9px] uppercase tracking-[0.2em]"
					style={{ color: BP.label }}
				>
					Pan · Scroll · Pinch to zoom
				</p>
			) : null}
		</div>
	);
}
